import { postOpsAnalyticsAPI } from "../../../../api/Networking";
import {
  PodRevenueAppliedFilters,
  PodRevenueSortBy,
  PodRevenueSortType,
  toPodRevenueRequestBody,
} from "../../../../models/Dashboard/PodRevenue/PodRevenueFilters";
import {
  PodRevenueLineRow,
  PodRevenueOrderRow,
  PodRevenuePagedResult,
  PodRevenueProductRow,
  PodRevenueSummaryResult,
} from "../../../../models/Dashboard/PodRevenue/PodRevenueSummary";

export default class PodRevenueDashboardAPI {
  private body = (
    applied: PodRevenueAppliedFilters,
    page: number,
    page_size: number,
    sort_by: PodRevenueSortBy,
    sort_type: PodRevenueSortType,
    product_id?: number,
  ) =>
    toPodRevenueRequestBody(
      applied,
      page,
      page_size,
      sort_by,
      sort_type,
      product_id,
    );

  getSummary = async (
    applied: PodRevenueAppliedFilters,
  ): Promise<PodRevenueSummaryResult> => {
    // Weekly gross trend UI is hidden for now. There is no separate /trend
    // FE call; trend used to arrive inside /summary — BE skips fetching it.
    const response = await postOpsAnalyticsAPI(
      "/v1/sales/pod-revenue/summary",
      this.body(applied, 1, 20, "gross_revenue", "DESC"),
    );
    if (!response.success || !response.result) {
      throw new Error(
        response.message || "Failed to load POD revenue summary.",
      );
    }
    return response.result as PodRevenueSummaryResult;
  };

  getProducts = async (
    applied: PodRevenueAppliedFilters,
    page: number,
    page_size: number,
    sort_by: PodRevenueSortBy,
    sort_type: PodRevenueSortType,
  ): Promise<PodRevenuePagedResult<PodRevenueProductRow>> => {
    const response = await postOpsAnalyticsAPI(
      "/v1/sales/pod-revenue/products",
      this.body(applied, page, page_size, sort_by, sort_type),
    );
    if (!response.success || !response.result) {
      throw new Error(
        response.message || "Failed to load POD revenue products.",
      );
    }
    return response.result as PodRevenuePagedResult<PodRevenueProductRow>;
  };

  /** Order listing (one row per order; handling/shipping from POD services). */
  getOrders = async (
    applied: PodRevenueAppliedFilters,
    page: number,
    page_size: number,
    sort_by: PodRevenueSortBy,
    sort_type: PodRevenueSortType,
  ): Promise<PodRevenuePagedResult<PodRevenueOrderRow>> => {
    const response = await postOpsAnalyticsAPI(
      "/v1/sales/pod-revenue/orders",
      this.body(applied, page, page_size, sort_by, sort_type),
    );
    if (!response.success || !response.result) {
      throw new Error(response.message || "Failed to load POD revenue orders.");
    }
    return response.result as PodRevenuePagedResult<PodRevenueOrderRow>;
  };

  getLines = async (
    applied: PodRevenueAppliedFilters,
    page: number,
    page_size: number,
    sort_by: PodRevenueSortBy,
    sort_type: PodRevenueSortType,
    product_id?: number,
  ): Promise<PodRevenuePagedResult<PodRevenueLineRow>> => {
    const response = await postOpsAnalyticsAPI(
      "/v1/sales/pod-revenue/lines",
      this.body(applied, page, page_size, sort_by, sort_type, product_id),
    );
    if (!response.success || !response.result) {
      throw new Error(response.message || "Failed to load POD revenue lines.");
    }
    return response.result as PodRevenuePagedResult<PodRevenueLineRow>;
  };
}
